import {
    IAccountSitesRules,
    IAddOnsRules,
    IDealTypeOverrides,
    IInfraRules,
    ITemplateUIRules,
    useGetTemplateUIRulesQuery,
} from "pages/Projects/ProjectServices"
import { createContext, ReactNode, useContext, useMemo } from "react"
import { useSelector } from "react-redux"
import { TStore } from "store/Store"

interface TemplateUIRulesContextValue {
    uiRules: ITemplateUIRules | undefined
    isFetching: boolean
    accountSitesRules: IAccountSitesRules
    addOnsRules: IAddOnsRules
    infraRules: IInfraRules
    dealTypeOverrides: IDealTypeOverrides
}

const defaultAccountSitesRules: IAccountSitesRules = {
    show_source_site: true,
    warehouse_first: false,
    source_site_pin: [],
    source_site_default: undefined,
}

const defaultAddOnsRules: IAddOnsRules = {
    manpower_enabled: true,
    om_required_fields_visible: true,
}

const defaultInfraRules: IInfraRules = {
    scope_required: true,
    scope_editable: true,
    scope_default: [],
    eva_fields_visible: true,
}

const defaultDealTypeOverrides: IDealTypeOverrides = {
    active: undefined,
    cpo: {},
    outright: {},
}

const TemplateUIRulesContext = createContext<TemplateUIRulesContextValue>({
    uiRules: undefined,
    isFetching: false,
    accountSitesRules: defaultAccountSitesRules,
    addOnsRules: defaultAddOnsRules,
    infraRules: defaultInfraRules,
    dealTypeOverrides: defaultDealTypeOverrides,
})

interface TemplateUIRulesProviderProps {
    children: ReactNode
}

export const TemplateUIRulesProvider = ({ children }: TemplateUIRulesProviderProps) => {
    const { deal_details_form } = useSelector((state: TStore) => state.ProjectForm)
    const templateId = deal_details_form?.template_id?.id

    const { data: uiRulesResponse, isFetching } = useGetTemplateUIRulesQuery(templateId!, {
        skip: !templateId,
    })

    const uiRules = uiRulesResponse?.ui_rules

    const accountSitesRules = useMemo<IAccountSitesRules>(() => {
        return {
            ...defaultAccountSitesRules,
            ...uiRules?.account_sites,
        }
    }, [uiRules?.account_sites])

    const addOnsRules = useMemo<IAddOnsRules>(() => {
        return {
            ...defaultAddOnsRules,
            ...uiRules?.add_ons,
            is_amc: uiRules?.is_amc,
            is_warranty: uiRules?.is_warranty,
        }
    }, [uiRules?.add_ons])

    const infraRules = useMemo<IInfraRules>(() => {
        return {
            ...defaultInfraRules,
            ...uiRules?.infra,
        }
    }, [uiRules?.infra])

    const dealTypeOverrides = useMemo<IDealTypeOverrides>(() => {
        return {
            ...defaultDealTypeOverrides,
            ...uiRules?.deal_type_overrides,
        }
    }, [uiRules?.deal_type_overrides])

    const value = useMemo<TemplateUIRulesContextValue>(
        () => ({
            uiRules,
            isFetching,
            accountSitesRules,
            addOnsRules,
            infraRules,
            dealTypeOverrides,
        }),
        [uiRules, isFetching, accountSitesRules, addOnsRules, infraRules, dealTypeOverrides]
    )

    return <TemplateUIRulesContext.Provider value={value}>{children}</TemplateUIRulesContext.Provider>
}

export const useTemplateUIRules = () => {
    const context = useContext(TemplateUIRulesContext)
    if (!context) {
        throw new Error("useTemplateUIRules must be used within a TemplateUIRulesProvider")
    }
    return context
}

export default TemplateUIRulesContext
